// app/src/scenes/LandingPage/components/BrandGuidelines/index.tsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, generatePath, useParams } from "react-router-dom";
import { Globe, Palette, Image, Users, LayoutTemplate, Sparkles, Sun, Camera } from "lucide-react";
import {
  apiPutBrandGuidelines,
  apiPostBrandGuidelinesCrawl,
  apiCreateBrandAsset,
  apiUpdateBrandAsset,
  apiDeleteBrandAsset,
  apiPostBrandTrain,
} from "services/apiService";
import { useBrandGuidelinesRealtime } from "hooks/useBrandGuidelinesRealtime";
import IdentityTab from "./IdentityTab";
import LogosTab from "./LogosTab";
import MannequinsTab from "./MannequinsTab";
import TemplatesTab from "./TemplatesTab";
import HeroAdsTab from "./HeroAdsTab";
import LightingTab from "./LightingTab";
import ProductShotsTab from "./ProductShotsTab";

const TABS = [
  { key: "identity", label: "Identity", icon: Palette, assetType: null },
  { key: "logos", label: "Logos", icon: Image, assetType: "logo" as const },
  { key: "mannequins", label: "Mannequins", icon: Users, assetType: "mannequin" as const },
  { key: "hero_ads", label: "Hero ads", icon: Sparkles, assetType: "hero_ad" as const },
  { key: "lighting", label: "Lighting", icon: Sun, assetType: "lighting_ref" as const },
  { key: "product_shots", label: "Product shots", icon: Camera, assetType: "product_shot_ref" as const },
  { key: "templates", label: "Templates", icon: LayoutTemplate, assetType: "template" as const },
] as const;

type TabKey = typeof TABS[number]["key"];

const DEBOUNCE_MS = 800;

const IDENTITY_FIELDS = [
  "colors",
  "fonts",
  "theme_color",
  "overall_guidelines",
  "voice_and_tone",
  "photography_style",
  "lighting_rules",
  "composition_rules",
  "do_not_list",
  "sources",
] as const;

const BrandGuidelinesEditor: React.FC = () => {
  const { project_id: projectId } = useParams<{ project_id: string }>();
  const navigate = useNavigate();

  const { guidelines, loading: isLoading, error: realtimeError, updateField, clearDirty, patchAsset, removeAsset } =
    useBrandGuidelinesRealtime(projectId);

  const [activeTab, setActiveTab] = useState<TabKey>("identity");
  const [error, setError] = useState<string | null>(null);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlUrl, setCrawlUrl] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [pendingTraining, setPendingTraining] = useState<TrainingResponse | null>(null);
  const [isTraining, setIsTraining] = useState(false);
  const [heroAdsAddedFromCrawl, setHeroAdsAddedFromCrawl] = useState(0);

  // Debounce timer for identity auto-save
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Accumulate patches between debounce fires
  const pendingPatch = useRef<Partial<BrandGuidelines>>({});

  // Flush pending save on unmount (don't lose edits)
  useEffect(() => {
    return () => {
      if (savedTimeout.current) clearTimeout(savedTimeout.current);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        const patchToSave = { ...pendingPatch.current };
        pendingPatch.current = {};
        if (Object.keys(patchToSave).length > 0 && projectId) {
          // Fire-and-forget: save pending edits before unmount
          apiPutBrandGuidelines(projectId, patchToSave as BrandGuidelinesUpsert).catch(console.error);
        }
      }
    };
  }, [projectId]);

  // Show realtime errors
  useEffect(() => {
    if (realtimeError) setError(realtimeError);
  }, [realtimeError]);

  const goBackToProject = () => {
    navigate(generatePath("/projects/:project_id", { project_id: projectId! }), { replace: true });
  };

  if (!projectId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-red-400 text-xl">Project ID is missing in the URL</div>
      </div>
    );
  }

  if (isLoading || !guidelines) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">{isLoading ? "Loading brand guidelines..." : "Failed to load"}</div>
      </div>
    );
  }

  const handleIdentityUpdate = (patch: Partial<BrandGuidelines>) => {
    // Update local state via the hook's updateField (marks fields dirty)
    for (const [key, value] of Object.entries(patch)) {
      if (IDENTITY_FIELDS.includes(key as typeof IDENTITY_FIELDS[number])) {
        updateField(key as typeof IDENTITY_FIELDS[number], value);
      }
    }

    // Accumulate the patch
    pendingPatch.current = { ...pendingPatch.current, ...patch };

    // Debounce the API save
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      const patchToSave = { ...pendingPatch.current };
      pendingPatch.current = {};

      setSaveStatus("saving");
      try {
        await apiPutBrandGuidelines(projectId, patchToSave as BrandGuidelinesUpsert);
        clearDirty(Object.keys(patchToSave).filter((k) =>
          IDENTITY_FIELDS.includes(k as typeof IDENTITY_FIELDS[number])
        ) as (typeof IDENTITY_FIELDS[number])[]);
        setSaveStatus("saved");
        if (savedTimeout.current) clearTimeout(savedTimeout.current);
        savedTimeout.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (err) {
        setError("Failed to save");
        setSaveStatus("idle");
        console.error(err);
      }
    }, DEBOUNCE_MS);
  };

  const handleCrawl = async () => {
    const u = crawlUrl.trim();
    if (!u || !/^https?:\/\//i.test(u)) { setError("Please enter a valid http(s) URL"); return; }
    setIsCrawling(true);
    setError(null);
    try {
      const result = await apiPostBrandGuidelinesCrawl(projectId, u);
      // Identity fields arrive via the Firestore snapshot listener. Logos and
      // derived visuals live in the assets subcollection which the listener
      // doesn't cover, so patch them from the POST response directly.
      result?.assets?.forEach(patchAsset);
      setHeroAdsAddedFromCrawl(result?.hero_ads_added ?? 0);
      setPendingTraining(null);
      setCrawlUrl("");
    } catch (err) {
      setError("Failed to crawl website");
      console.error(err);
    } finally {
      setIsCrawling(false);
    }
  };

  // Asset handlers — each returns the mutated asset so we can patch local
  // state directly. This avoids a REST refetch and sidesteps a race where
  // the Firestore snapshot listener can reset guidelines.assets to the
  // pre-mutation value after a full refetch.
  const handleAssetUpload = (assetType: BrandAssetType) => async (file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    fd.append("type", assetType);
    fd.append("name", file.name.replace(/\.[^.]+$/, ""));
    // hero_ad requires metadata.attribution — default new uploads to "own".
    if (assetType === "hero_ad") {
      fd.append("metadata", JSON.stringify({ attribution: "own" }));
    }
    try {
      const created = await apiCreateBrandAsset(projectId, fd);
      patchAsset(created);
      setError(null);
    } catch (err) {
      setError("Failed to upload asset");
      console.error(err);
      throw err;
    }
  };

  const handleAssetDelete = async (assetId: string) => {
    try {
      await apiDeleteBrandAsset(projectId, assetId);
      removeAsset(assetId);
      setError(null);
    } catch (err) {
      setError("Failed to delete asset");
      console.error(err);
      throw err;
    }
  };

  const handleAssetUpdate = async (assetId: string, updates: FormData) => {
    // Optimistic update — the backend round-trip regenerates two GCS
    // signed URLs (~400-1000ms total), so we patch local state first.
    const previous = guidelines.assets.find((a) => a.id === assetId);
    if (previous) {
      const optimistic: BrandAsset = { ...previous };
      const nameRaw = updates.get("name");
      if (typeof nameRaw === "string") optimistic.name = nameRaw;
      const tagsRaw = updates.get("tags");
      if (typeof tagsRaw === "string") {
        try { optimistic.tags = JSON.parse(tagsRaw); } catch { /* ignore */ }
      }
      const metaRaw = updates.get("metadata");
      if (typeof metaRaw === "string") {
        try { optimistic.metadata = JSON.parse(metaRaw); } catch { /* ignore */ }
      }
      patchAsset(optimistic);
    }

    try {
      const updated = await apiUpdateBrandAsset(projectId, assetId, updates);
      patchAsset(updated);
      setError(null);
    } catch (err) {
      if (previous) patchAsset(previous);
      setError("Failed to update asset");
      console.error(err);
    }
  };

  const handleTrain = async () => {
    if (!projectId) return;
    setIsTraining(true);
    setError(null);
    try {
      const resp = await apiPostBrandTrain(projectId);
      setPendingTraining(resp);
      setHeroAdsAddedFromCrawl(0);
    } catch (err: any) {
      if (err?.error === "no_trainable_assets") {
        setError(err.detail || "Upload at least 2 reference assets (hero ads, lighting refs, product shots, mannequins).");
      } else if (err?.error === "insufficient_samples") {
        const skipped: { field: string; reason: string }[] = err.skipped_fields ?? [];
        const fieldLabels: Record<string, string> = {
          voice_and_tone: "Voice & tone",
          photography_style: "Photography style",
          lighting_rules: "Lighting rules",
          composition_rules: "Composition rules",
          do_not_list: "Do-not list",
          suggested_colors_to_add: "Colors",
          suggested_fonts_to_add: "Fonts",
        };
        const lines = skipped.map(
          (s) => `${fieldLabels[s.field] ?? s.field}: ${s.reason}`
        );
        setError(
          "Not enough reference assets for any brand rule yet. Upload more and retry.\n" +
          (lines.length ? "\n" + lines.join("\n") : "")
        );
      } else {
        setError("Training failed. Please try again.");
      }
      console.error(err);
    } finally {
      setIsTraining(false);
    }
  };

  const handleDiscardTraining = () => {
    setPendingTraining(null);
  };

  return (
    <div className="min-h-screen text-gray-100 flex flex-col">
      <main className="flex-grow w-full max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {guidelines.project_name || "Brand Guidelines"}
            </h1>
            <p className="text-gray-400">Brand Guidelines Editor</p>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300" onClick={goBackToProject}>
            Go back to project
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center justify-between">
            <p className="text-red-400 whitespace-pre-wrap">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 ml-4" aria-label="Dismiss error">✕</button>
          </div>
        )}

        {heroAdsAddedFromCrawl > 0 && (
          <div className="mb-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 flex items-center justify-between">
            <p className="text-purple-200">
              {heroAdsAddedFromCrawl} hero ad{heroAdsAddedFromCrawl > 1 ? "s" : ""} detected from crawl — train brand rules?
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleTrain}
                disabled={isTraining}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg"
              >
                {isTraining ? "Training…" : "Train brand rules"}
              </button>
              <button
                onClick={() => setHeroAdsAddedFromCrawl(0)}
                className="text-purple-300 hover:text-purple-100 px-2"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Crawl bar */}
        <div className="mb-6 bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <div className="flex gap-2">
            <input
              type="url" value={crawlUrl} onChange={(e) => setCrawlUrl(e.target.value)}
              placeholder="Enter website URL to auto-detect brand identity"
              className="flex-grow bg-gray-900 border border-gray-700 text-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
            <button
              onClick={handleCrawl} disabled={isCrawling || !crawlUrl.trim()}
              className="bg-gradient-to-br from-purple-600 to-purple-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/40 hover:-translate-y-px active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              <Globe className="w-5 h-5" /> {isCrawling ? "Crawling..." : "Auto-Detect"}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Extracts brand colors, fonts, and guidelines from your website text. Also downloads logos and hero images as reference assets.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-700">
          <div className="flex gap-1">
            {TABS.map(({ key, label, icon: Icon, assetType }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === key
                    ? "bg-gray-800 text-white border-b-2 border-blue-500"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
                {assetType && (
                  <span className="text-xs bg-gray-700 text-gray-300 rounded-full px-1.5 py-0.5 ml-1">
                    {guidelines.assets.filter((a) => a.type === assetType).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        {activeTab === "identity" && (
          <IdentityTab
            guidelines={guidelines}
            onUpdate={handleIdentityUpdate}
            saveStatus={saveStatus}
            trainableAssetCount={guidelines.assets.filter(
              (a) => a.type === "hero_ad" || a.type === "lighting_ref"
                 || a.type === "product_shot_ref" || a.type === "mannequin"
            ).length}
            isTraining={isTraining}
            pendingTraining={pendingTraining}
            onTrainClick={handleTrain}
            onDiscardTraining={handleDiscardTraining}
          />
        )}
        {activeTab === "logos" && (
          <LogosTab assets={guidelines.assets} projectId={projectId} onUpload={handleAssetUpload("logo")} onDelete={handleAssetDelete} onUpdate={handleAssetUpdate} />
        )}
        {activeTab === "mannequins" && (
          <MannequinsTab assets={guidelines.assets} projectId={projectId} onUpload={handleAssetUpload("mannequin")} onDelete={handleAssetDelete} onUpdate={handleAssetUpdate} />
        )}
        {activeTab === "hero_ads" && (
          <HeroAdsTab assets={guidelines.assets} projectId={projectId} onUpload={handleAssetUpload("hero_ad")} onDelete={handleAssetDelete} onUpdate={handleAssetUpdate} />
        )}
        {activeTab === "lighting" && (
          <LightingTab assets={guidelines.assets} projectId={projectId} onUpload={handleAssetUpload("lighting_ref")} onDelete={handleAssetDelete} onUpdate={handleAssetUpdate} />
        )}
        {activeTab === "product_shots" && (
          <ProductShotsTab assets={guidelines.assets} projectId={projectId} onUpload={handleAssetUpload("product_shot_ref")} onDelete={handleAssetDelete} onUpdate={handleAssetUpdate} />
        )}
        {activeTab === "templates" && (
          <TemplatesTab assets={guidelines.assets} projectId={projectId} onUpload={handleAssetUpload("template")} onDelete={handleAssetDelete} onUpdate={handleAssetUpdate} />
        )}

        {/* Footer */}
        <div className="mt-6 text-center text-gray-500 text-sm">
          {guidelines.updated_at ? `Last updated: ${new Date(guidelines.updated_at).toLocaleString()}` : ""}
        </div>
      </main>
    </div>
  );
};

export default BrandGuidelinesEditor;
